import ChestRepository from "@/game/ChestRepository";
import { ResourceType } from "@/game/entities/config/resources";
import { increaseExplorationSpeed } from "@/game/utils/upgrades";
import useSegmentManager from "@/hooks/useSegmentManager";
import useTileRepository from "@/hooks/useTileRepository";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import {
	type ProductionValue,
	type TileConfig,
	type TileConfigItem,
	type TileCoordinates,
	type UpgradeParams,
} from "@/types/tile";
import { FrameName } from ".";
import { ChestType } from "../objects/chests";
import { UpgradeIds } from "./upgrades";

/** CONFIG **/

const config: TileConfig = {
	// Kubo 5
	[FrameName.PARTLY_DEMOLISHED_INN1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.FOOD_INCOME, 50],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		reward: {
			[ResourceType.COINS]: 10_000,
		},
		rewardText: ["ui.food_production_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+50",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.HILL_GALLOWS1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.exploration_speed_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+1",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			increaseExplorationSpeed(1);
		},
	},
	[FrameName.CAMP_SIEGE1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		reward: {
			[ResourceType.COINS]: 100_000,
			[ResourceType.LUMBER]: 15_000,
			[ResourceType.STONE]: 10_000,
			[ResourceType.IRON]: 5_000,
		},
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.CAMP_SIEGE2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.CAMP_SIEGE3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.CAMP_SIEGE4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MONASTERY_ABANDONED1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		reward: {
			[ResourceType.COINS]: 150_000,
		},
		rewardText: ["ui.exploration_speed_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+4",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			increaseExplorationSpeed(4);
		},
	},
	[FrameName.MONASTERY_ABANDONED2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MONASTERY_ABANDONED3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MONASTERY_ABANDONED4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.FOREST_WHISPERING1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["terrain.forest_number_description"],
		rewardTextReplace: [
			{
				"{number}": "8",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.FOREST_WHISPERING2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.FOREST_WHISPERING3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.FOREST_WHISPERING4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.FOREST_WHISPERING5]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.FOREST_WHISPERING6]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.FOREST_WHISPERING7]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.FOREST_WHISPERING8]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.FOREST_WHISPERING9]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 8],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
		]),
	},
	[FrameName.WATERFALL1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 500],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.coin_income_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+500",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.WATERFALL2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.SPRING_SACRED1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.FOOD_INCOME, 250],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.food_production_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+250",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.WATCHPOST_BURNED1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>([[ResourceType.ARMY, 500]]),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.accomodates"],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.TREE_ANCIENT1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>([[ResourceType.LUMBER, 5_000]]),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.stores"],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.CARAVAN_RUINS1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		reward: {
			[ResourceType.COINS]: 50_000,
		},
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.CARAVAN_RUINS2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.CRYPT_NOBLE1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		reward: {
			[ResourceType.COINS]: 500_000,
		},
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.CRYPT_NOBLE2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.STORAGE_ABANDONED1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>([
			[ResourceType.LUMBER, 30_000],
			[ResourceType.STONE, 25_000],
			[ResourceType.CLAY, 20_000],
			[ResourceType.IRON, 15_000],
			[ResourceType.GOODS, 10_000],
		]),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.stores"],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.STORAGE_ABANDONED2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.CAVE_SHADOWS1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.exploration_speed_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+25",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			increaseExplorationSpeed(25);
		},
	},
	[FrameName.SUNDIAL_FORGOTTEN1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["upgrade.max_offline_time_description"],
		rewardTextReplace: [
			{
				"{number}": "30",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.ALCHEMIST_ABANDONED1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.exploration_speed_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+6",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			increaseExplorationSpeed(6);
		},
	},
	[FrameName.ALCHEMIST_ABANDONED2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ALCHEMIST_ABANDONED3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ALCHEMIST_ABANDONED4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.FORTRESS_SUNKEN1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.swamp_roads", "ui.2to4_special_chests"],
		rewardTextReplace: [
			{},
			{
				"{2to4}": "2",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(UpgradeIds.embankment);

			const chestrepo = ChestRepository.getInstance();
			if (!chestrepo.initialized) {
				chestrepo.initialize(useTileRepository());
			}
			for (let i = 0; i < 2; i++) {
				chestrepo.tryGenerateChest(ChestType.FIXED);
			}
		},
	},
	[FrameName.FORTRESS_SUNKEN2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.FORTRESS_SUNKEN3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.FORTRESS_SUNKEN4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.QUARRY_FLOODED1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>([[ResourceType.STONE, 25_000]]),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.stores"],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.QUARRY_FLOODED2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.QUARRY_FLOODED3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.QUARRY_FLOODED4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MONUMENT_MEGALITHIC1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["upgrade.building_line_of_sight_description"],
		rewardTextReplace: [
			{
				"{building}": `${FrameName.HILLS_SCOUT_TOWER}.name`,
				"{number}": "1",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(
				UpgradeIds.line_of_sight,
			);
		},
	},
	[FrameName.MONUMENT_MEGALITHIC2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MONUMENT_MEGALITHIC3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MONUMENT_MEGALITHIC4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.WHIRLPOOL1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.exploration_speed_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+12",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			increaseExplorationSpeed(12);
		},
	},
	[FrameName.WHIRLPOOL2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.WHIRLPOOL3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.WHIRLPOOL4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.CASTAWAY_ISLAND1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		reward: {
			[ResourceType.GEMS]: 200,
		},
		rewardText: ["ui.2to4_special_chests"],
		rewardTextReplace: [
			{
				"{2to4}": "3",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			const chestrepo = ChestRepository.getInstance();
			if (!chestrepo.initialized) {
				chestrepo.initialize(useTileRepository());
			}
			for (let i = 0; i < 3; i++) {
				chestrepo.tryGenerateChest(ChestType.FIXED);
			}
		},
	},
	[FrameName.OUTPOST_SEA1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.reward.non_translatable", "ui.2to4_special_chests"],
		rewardTextReplace: [
			{
				"{building}": `${FrameName.SHALLOW_SEA_SMALL_FISH_TRAP}.name`,
				"{number}": "10x",
			},
			{
				"{2to4}": "2",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useBuildingsStorageStore().increase(
				FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
				10,
				"poi reward",
			);

			const chestrepo = ChestRepository.getInstance();
			if (!chestrepo.initialized) {
				chestrepo.initialize(useTileRepository());
			}
			for (let i = 0; i < 2; i++) {
				chestrepo.tryGenerateChest(ChestType.FIXED);
			}
		},
	},
	[FrameName.OUTPOST_SEA2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.OUTPOST_SEA3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.OUTPOST_SEA4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},

	[FrameName.KEEP_DESERTED1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>([[ResourceType.ARMY, 2500]]),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.accomodates"],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.KEEP_DESERTED2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.KEEP_DESERTED3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.KEEP_DESERTED4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MOBY_DICK1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 50],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.fishing_source"],
		rewardTextReplace: [
			{
				"{number}": "25", // polovica income lebo male ryby davaju 2 jedla
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
		supplies: new Set<FrameName>([
			FrameName.SHALLOW_SEA_FISHING_BOAT,
			FrameName.DEEP_SEA_FISHING_BOAT,
			FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			FrameName.DEEP_SEA_BIG_FISHING_BOAT,
		]),
	},
	[FrameName.MOBY_DICK2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 50],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.SHALLOW_SEA_FISHING_BOAT,
			FrameName.DEEP_SEA_FISHING_BOAT,
			FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			FrameName.DEEP_SEA_BIG_FISHING_BOAT,
		]),
	},
	[FrameName.MOBY_DICK3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 50],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.SHALLOW_SEA_FISHING_BOAT,
			FrameName.DEEP_SEA_FISHING_BOAT,
			FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			FrameName.DEEP_SEA_BIG_FISHING_BOAT,
		]),
	},
	[FrameName.MOBY_DICK4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 50],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		supplies: new Set<FrameName>([
			FrameName.SHALLOW_SEA_FISHING_BOAT,
			FrameName.DEEP_SEA_FISHING_BOAT,
			FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			FrameName.DEEP_SEA_BIG_FISHING_BOAT,
		]),
	},
	[FrameName.BIG_MUSHROOM1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["upgrade.building_production_description"],
		rewardTextReplace: [
			{
				"{building}": `${FrameName.UNIVERSITY}.name`,
				"{percent}": "40%",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(UpgradeIds.mushroom);

			const tr = useTileRepository();
			const sm = useSegmentManager();

			[...tr.getTileCoordinates2(FrameName.UNIVERSITY)].forEach(
				({ x, y }: TileCoordinates): void =>
					sm.markSegmentAsDirtyByCoordinates({ x, y }),
			);

			// sm.run(true); // will run later
		},
	},
	[FrameName.BIG_MUSHROOM2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.BIG_MUSHROOM3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.BIG_MUSHROOM4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ASSASINS_HIDEOUT1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.reward.non_translatable"],
		rewardTextReplace: [
			{
				"{building}": `${FrameName.PREMIUM_GOLD_MINE}.name`,
				"{number}": "3x",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useBuildingsStorageStore().increase(
				FrameName.PREMIUM_GOLD_MINE,
				3,
				"poi reward",
			);
		},
	},
	[FrameName.ASSASINS_HIDEOUT2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ASSASINS_HIDEOUT3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ASSASINS_HIDEOUT4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PIRATES_HIDEOUT1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.sea_exploration_discount"],
		rewardTextReplace: [
			{
				"{percent}": "15%",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(
				UpgradeIds.pirate_navigation,
			);
		},
	},
	[FrameName.PIRATES_HIDEOUT2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PIRATES_HIDEOUT3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PIRATES_HIDEOUT4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PIRATES_HIDEOUT5]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PIRATES_HIDEOUT6]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PIRATES_HIDEOUT7]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PIRATES_HIDEOUT8]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PIRATES_HIDEOUT9]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ASTRONOMER_HIDEOUT1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.exploration_speed_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+50",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			increaseExplorationSpeed(50);
		},
	},
	[FrameName.ASTRONOMER_HIDEOUT2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ASTRONOMER_HIDEOUT3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ASTRONOMER_HIDEOUT4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MOUNTAIN_LAKE1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		reward: {
			[ResourceType.AD_SKIPPER]: 15,
		},
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.MOUNTAIN_LAKE2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MOUNTAIN_LAKE3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MOUNTAIN_LAKE4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ATOL1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["upgrade.building_production_description"],
		rewardTextReplace: [
			{
				// Plati aj pre shallow aj pre deep.
				"{building}": `${FrameName.SHALLOW_SEA_PEARL_HUNTER}.name`,
				"{percent}": "25%",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(UpgradeIds.atol);

			const tr = useTileRepository();
			const sm = useSegmentManager();

			[
				...tr.getTileCoordinates2(FrameName.SHALLOW_SEA_PEARL_HUNTER),
				...tr.getTileCoordinates2(FrameName.DEEP_SEA_PEARL_HUNTER),
			].forEach(({ x, y }: TileCoordinates): void =>
				sm.markSegmentAsDirtyByCoordinates({ x, y }),
			);

			// sm.run(true); // will run later
		},
	},
	[FrameName.ATOL2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ATOL3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ATOL4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ATOL5]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ATOL6]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ATOL7]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ATOL8]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.ATOL9]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.BEAR_SKELETON1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		reward: {
			[ResourceType.SUPER_CLICKER]: 500,
		},
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.CRATER1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["upgrade.building_production_description"],
		rewardTextReplace: [
			{
				"{building}": `${FrameName.UNIVERSITY}.name`,
				"{percent}": "60%",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(UpgradeIds.crater);

			const tr = useTileRepository();
			const sm = useSegmentManager();

			[...tr.getTileCoordinates2(FrameName.UNIVERSITY)].forEach(
				({ x, y }: TileCoordinates): void =>
					sm.markSegmentAsDirtyByCoordinates({ x, y }),
			);

			// sm.run(true); // will run later
		},
	},
	[FrameName.CRATER2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.CRATER3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.CRATER4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PLAGUED_FOREST1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number =>
					50 *
					(useTileRepository().getAllExploredPoi1TileIndexes().size ??
						0),
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.coin_income_per_discovered_monument"],
		rewardTextReplace: [
			{
				"{number}": "+50",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.PLAGUED_FOREST2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PLAGUED_FOREST3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.PLAGUED_FOREST4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.MENHIR1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 1_500],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.coin_income_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+1500",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.MENHIR2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.FALCONERS_TOWER1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.FOOD_INCOME, 2_000],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.exploration_speed_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+100",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			increaseExplorationSpeed(100);
		},
	},
	[FrameName.FALCONERS_TOWER2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.TERRACE_GARDENS1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.FOOD_INCOME, 2_000],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.food_production_bonus"],
		rewardTextReplace: [
			{
				"{number}": "+2000",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {},
	},
	[FrameName.TERRACE_GARDENS2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.TERRACE_GARDENS3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.TERRACE_GARDENS4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.TERRACE_GARDENS5]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.TERRACE_GARDENS6]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.TERRACE_GARDENS7]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.TERRACE_GARDENS8]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.TERRACE_GARDENS9]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.KRAKEN1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["upgrade.min_new_town_level_description"],
		rewardTextReplace: [
			{
				"{number}": "10",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(UpgradeIds.kraken);
		},
	},
	[FrameName.KRAKEN2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.KRAKEN3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.KRAKEN4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.WITCH_LAKE1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: ["ui.daily_triple_income_limit_increase"],
		rewardTextReplace: [
			{
				"{number}": "2",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(UpgradeIds.witch_lake);
		},
	},
	[FrameName.GEYSER1]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		rewardText: [
			"upgrade.chest_content_description",
			"ui.2to4_special_chests",
		],
		rewardTextReplace: [
			{
				"{percent}": "25%",
			},
			{
				"{2to4}": "3",
			},
		],
		// hlavny tile poi musi mat vzdy onReveal, aj ak je prazdny
		onReveal: (): void => {
			useUpgradesStore().increaseCurrentLevelById(UpgradeIds.geyser);

			const chestrepo = ChestRepository.getInstance();
			if (!chestrepo.initialized) {
				chestrepo.initialize(useTileRepository());
			}
			for (let i = 0; i < 3; i++) {
				chestrepo.tryGenerateChest(ChestType.FIXED);
			}
		},
	},
	[FrameName.GEYSER2]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.GEYSER3]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.GEYSER4]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
};

export const getPoiConfig = (frameName: FrameName): TileConfigItem => {
	const poiConfig = config?.[frameName];
	if (!poiConfig)
		throw new Error(`POI config not found for type: ${frameName}!`);

	return poiConfig;
};

export const isPoi = (frameName: FrameName): boolean => frameName in config;
